import React, { useEffect, useState, useRef } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { setSearchStore } from "../../../redux/Reducer/Product/ProductSlice";
import { getAllDoctorsOfStore } from "../../../redux/Thunk/Doctor/DoctorThunks";
import { setStoreById } from "../../../redux/Reducer/Store/StoreSlice";
import { setDoctorsAssociatedWithStore } from "../../../redux/Reducer/Doctor/DoctorSlice";
import { getAllProducts } from "../../../redux/Thunk/Product/ProductThunks";

const SearchSelectStore = ({
  type,
  setISViewIntegrateDoctor,
  selectedStoreIds = [],
  onStoreChange,
  placeholder = "Filter by stores...",
  label = null,
  labelClassName = "block text-primary-400 text-[13px] leading-[20px]",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);

  const { stores } = useSelector(
    ({ store }) => ({
      stores: store?.stores || [],
    }),
    shallowEqual
  );

  const reduxSearchStore = useSelector((state) => state?.product?.searchStore);
  const PID = useSelector((state) => state?.product?.searchProduct?.[0])?.IID;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Determine active selected store IDs array
  const activeSelectedIds = React.useMemo(() => {
    if (Array.isArray(selectedStoreIds) && selectedStoreIds.length > 0) {
      return selectedStoreIds;
    }
    if (typeof selectedStoreIds === "string" && selectedStoreIds.trim()) {
      return selectedStoreIds.split(",").map((s) => s.trim()).filter(Boolean);
    }
    if (reduxSearchStore && typeof reduxSearchStore === "string" && reduxSearchStore.trim()) {
      return reduxSearchStore.split(",").map((s) => s.trim()).filter(Boolean);
    }
    return [];
  }, [selectedStoreIds, reduxSearchStore]);

  const selectedStores = React.useMemo(() => {
    return activeSelectedIds
      .map((sid) => stores.find((s) => String(s.SID) === String(sid)))
      .filter(Boolean);
  }, [activeSelectedIds, stores]);

  const filteredStores = stores.filter((store) => {
    const search = searchTerm.toLowerCase();
    return (
      store?.title?.toLowerCase().includes(search) ||
      String(store?.SID)?.toLowerCase().includes(search) ||
      store?.address?.city?.toLowerCase().includes(search)
    );
  });

  const handleToggleStore = async (store) => {
    const isSelected = activeSelectedIds.some((id) => String(id) === String(store.SID));
    let updatedIds;
    if (isSelected) {
      updatedIds = activeSelectedIds.filter((id) => String(id) !== String(store.SID));
    } else {
      updatedIds = [...activeSelectedIds, String(store.SID)];
    }

    const sidsString = updatedIds.join(",");

    if (onStoreChange) {
      onStoreChange(updatedIds);
    }

    if (type === "stock") {
      dispatch(setSearchStore(sidsString));
      if (updatedIds.length === 1) {
        const found = stores.find((s) => String(s.SID) === String(updatedIds[0]));
        if (found) dispatch(setStoreById(found));
      } else {
        dispatch(setStoreById({}));
      }
      if (!onStoreChange) {
        dispatch(getAllProducts({ current_page: 1, SID: sidsString, PID }));
      }
    } else if (type === "integration") {
      if (!isSelected) {
        dispatch(setStoreById(store));
        dispatch(getAllDoctorsOfStore(store?.SID));
      } else {
        dispatch(setDoctorsAssociatedWithStore([]));
        dispatch(setStoreById({}));
        if (setISViewIntegrateDoctor) setISViewIntegrateDoctor(false);
      }
      setIsOpen(false);
    }
  };

  const handleRemoveStore = (e, storeSid) => {
    e.stopPropagation();
    const updatedIds = activeSelectedIds.filter((id) => String(id) !== String(storeSid));
    const sidsString = updatedIds.join(",");

    if (onStoreChange) {
      onStoreChange(updatedIds);
    }

    if (type === "stock") {
      dispatch(setSearchStore(sidsString));
      if (!onStoreChange) {
        dispatch(getAllProducts({ current_page: 1, SID: sidsString, PID }));
      }
    } else if (type === "integration") {
      dispatch(setDoctorsAssociatedWithStore([]));
      dispatch(setStoreById({}));
      if (setISViewIntegrateDoctor) setISViewIntegrateDoctor(false);
    }
  };

  return (
    <div className="w-full relative" ref={dropdownRef}>
      {label && <label className={labelClassName}>{label}</label>}

      {/* Selected Stores Box */}
      <div
        className="min-h-[42px] p-2 bg-white border border-primary-200 rounded-[8px] flex flex-wrap items-center gap-1.5 cursor-pointer hover:border-primary-400 transition-all shadow-2xs"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedStores.length > 0 ? (
          selectedStores.map((store) => (
            <span
              key={store.SID}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium bg-emerald-600 text-white shadow-xs transition-all"
            >
              <span className="font-mono text-[11px] opacity-85">#{store.SID}</span>
              <span>{store.title}</span>
              <button
                type="button"
                onClick={(e) => handleRemoveStore(e, store.SID)}
                className="hover:opacity-75 focus:outline-none ml-0.5"
              >
                ✕
              </button>
            </span>
          ))
        ) : (
          <span className="text-[13px] text-gray-400 font-normal italic">
            {placeholder}
          </span>
        )}
      </div>

      {/* Popover Dropdown */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-primary-200 rounded-[8px] shadow-xl z-50 p-2.5 max-h-[300px] flex flex-col">
          <div className="pb-2 mb-2 border-b border-gray-100 flex items-center justify-between gap-2">
            <input
              type="text"
              placeholder="Search store name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full text-[12px] p-1.5 border border-primary-200 rounded bg-gray-50 focus:bg-white focus:outline-none focus:border-primary-500"
              autoFocus
            />
          </div>

          <div className="overflow-y-auto max-h-[220px] flex flex-col gap-1">
            {filteredStores.length > 0 ? (
              filteredStores.map((store) => {
                const isSelected = activeSelectedIds.some(
                  (id) => String(id) === String(store.SID)
                );
                return (
                  <button
                    key={store.SID}
                    type="button"
                    onClick={() => handleToggleStore(store)}
                    className={`w-full text-left px-3 py-2 text-[12px] rounded-[6px] flex items-center justify-between transition-colors ${
                      isSelected
                        ? "bg-emerald-50 text-emerald-900 font-semibold border border-emerald-200"
                        : "text-gray-700 hover:bg-gray-100 font-medium"
                    }`}
                  >
                    <div className="flex flex-col min-w-0 pr-2">
                      <div className="flex items-center gap-1.5 truncate">
                        <span className="px-1.5 py-0.5 bg-primary-100 text-primary-800 font-mono text-[10px] font-bold rounded">
                          #{store.SID}
                        </span>
                        <span className="truncate">{store.title}</span>
                      </div>
                      {store.address?.areaRoad && (
                        <span className="text-[11px] text-gray-400 truncate mt-0.5">
                          {store.address.areaRoad}, {store.address.city}
                        </span>
                      )}
                    </div>
                    <span
                      className={`text-[12px] font-bold shrink-0 ${
                        isSelected ? "text-emerald-600" : "text-gray-400"
                      }`}
                    >
                      {isSelected ? "✓" : "+"}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="text-[12px] text-gray-400 italic p-3 text-center">
                No stores found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchSelectStore;
